import { Fragment, useMemo, useState, useEffect } from "react";
import { BasicTable } from "../common/BasicTable";
import { useQuery } from "@tanstack/react-query";
import LoadingPage from "../common/LoadingPage";
import ErrorPage from "../common/ErrorPage";
import { useSeatSearchContext } from "../../store/SeatSearchContextProvider";
import { SeatFilterMap } from "./FilterComponents/FilterConstants";
import { useSeatData } from "../../store/MasterDataContext";
import { FilterButton } from "./SearchForm";
import { SeatsVerticalPanel } from "./FilterComponents/SeatsVerticalPanel";
import { checkApi } from "../../Authentication/AxiosInstance";
import { Accordion, AccordionHeader, AccordionBody, Button } from "@material-tailwind/react";
import { ChevronDownIcon, FunnelIcon, ArrowUpIcon, ArrowDownIcon, BarsArrowUpIcon } from "@heroicons/react/24/outline";
import DisclaimerBanner from "../common/Disclaimers";

export const SeatsMobileFirst = () => {
  const [filterOpen, setFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState(null); // 'instname', 'branchname', 'vacantseats', etc.
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const itemsPerPage = 10;
  
  const filterMap = SeatFilterMap;
  console.log(filterMap);
  const { seatMasterData } = useSeatData();
  const searchObject = useSeatSearchContext().seatSearchArray;
  console.log(searchObject);
  const getSearchKey = (searchObject) => JSON.stringify(searchObject);
  console.log(getSearchKey(searchObject))
  const searchArray = [searchObject];
  console.log(searchArray);

  const fetchSeats = async () => {
    const searchParams = createSearchParams(searchArray);
    // Convert URLSearchParams to plain object for axios
    const params = Object.fromEntries(searchParams);
    
    const res = await checkApi.get('/capzone/seats/', { params });
    return res.data;
  };

  const { data, error, status } = useQuery({
    queryKey: ["seatsData", getSearchKey(searchObject)],
    queryFn: fetchSeats,
  });

  // Reset pagination when data changes
  useEffect(() => {
    setCurrentPage(0);
  }, [data]);

  // Handle sort click
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(0); // Reset to first page when sorting
  };

  // Get sort icon
  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <BarsArrowUpIcon className="h-4 w-4 text-gray-400" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUpIcon className="h-4 w-4 text-primary-600" />
      : <ArrowDownIcon className="h-4 w-4 text-primary-600" />;
  };

  const nullFilter = (row, columnId, filterValue) => {
        const cellValue = row.getValue(columnId);

        if (filterValue.toLowerCase() === "null" || filterValue.toLowerCase() === "empty") {
          return cellValue === null || cellValue === undefined || cellValue === "";
        }

        return String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
  };
  
  const columns = useMemo(() => {
    if (!data || data.length === 0) return [];
    const v_allocationBoard = data[0]?.s001_allocationboard;
    const baseColumns = [
      {
        header: "Institute",
        footer: "Institute",
        accessorFn: (row) => `(${row.s001_instcode}) ${row.s001_instname}`,
        minSize: 600,
      },
      {
        accessorFn: (row) => (row.s001_branchcode !== null ? `(${row.s001_branchcode}) ${row.s001_branchname}` : `${row.s001_branchname}`),
        header: "Branch",
        footer: "Branch",
        minSize: 400,
      },
      {
        accessorKey: "s001_insttype",
        header: "Institute Type",
        footer: "Institute Type",
        cell: (row) => {
          return <div className="text-center">{row.getValue("s001_statequota")}</div>;
        },
        filterFn: nullFilter,
      },
      {
        accessorKey: "s001_statecriteria",
        header: "State Criteria",
        footer: "State Criteria",
        cell: (row) => {
          return <div className="text-center">{row.getValue("s001_statecriteria")}</div>;
        },
      },
     
    ];
    if (v_allocationBoard !== 'COMEDK') {
        baseColumns.push (
            {
              accessorKey: "s001_caste",
              header: "Category",
              footer: "Category",
              cell: (row) => {
                return <div className="text-center">{row.getValue("s001_category")}</div>;
              },
              enableColumnFilter: false,
            },
            {
              accessorKey: "s001_gender",
              header: "Gender",
              footer: "Gender",
              cell: (row) => {
                return <div className="text-center">{row.getValue("s001_gender")}</div>;
              },
              enableColumnFilter: false,
            },
            {
              accessorKey: "s001_common",
              header: "Common",
              footer: "Common",
              cell: (row) => {
                return <div className="text-center">{row.getValue("s001_common")}</div>;
              },
              filterFn: nullFilter,
            },
        );
      }
    if (v_allocationBoard === 'MHTCET') {
          baseColumns.push (
            {
              accessorKey: "s001_summary",
              header: "Summary",
              footer: "Summary",
              cell: (row) => {
                return <div className="text-center">{row.getValue("s001_summary")}</div>;
              },
              filterFn: nullFilter,
            },
          );
      }
    baseColumns.push (
            {
              accessorKey: "s001_vacantseats",
              header: "Vacant Seats",
              footer: "Vacant Seats",
              cell: (row) => {
                return <div className="text-center">{row.getValue("s001_vacantseats")}</div>;
              },
            },
          );
    return baseColumns;
    
  }, [data]);

  function createSearchParams(params) {
    const searchParams = new URLSearchParams();
    params.forEach((item) => {
      Object.keys(item).forEach((paramKey) => {
        if (item[paramKey]) {
          const findSeatParent = filterMap.find((seatKey) => seatKey.parent === paramKey);
          const paramSeatName = findSeatParent ? findSeatParent.seats : null;
          if (paramSeatName) {
            searchParams.append(paramSeatName + "__icontains", item[paramKey]);
            console.log(searchParams);
          }
        }
      });
    });
    return searchParams;
  }

  function matchedRow() {
    console.log(seatMasterData);
    const filterRow = seatMasterData.filter((item) => {
      return Object.keys(searchObject).every((key) => {
        let itemValue = item[key === "category" ? "caste" : key];
        let searchValue = searchObject[key];

        itemValue = !itemValue && itemValue !== 0 && itemValue !== false ? null : itemValue;
        searchValue = !searchValue && searchValue !== 0 && searchValue !== false ? null : searchValue;

        return itemValue === searchValue;
      });
    
    });

    if (filterRow.length > 0) {
      console.log(filterRow)
      const totalVacantSeats = filterRow.reduce((sum, row) => { 
        return sum + (Number(row.vacantseats) || 0);
      }, 0);
      return totalVacantSeats;
    } else {
      console.log("No matching rows found.");
      return undefined;
    }
  }

  const ShowSearchCriteria = () => {
    return Object.entries(searchObject).map(([key, value]) => {
      console.log(key, value);
      if (value !== null && value !== undefined) {
        return (
          <div key={key} className="inline-flex items-center px-2 py-1 rounded-full bg-primary-100 text-primary-900 text-xs font-semibold border border-primary-300">
            <span>{value}</span>
            <span className="ml-1 text-primary-600">&#x2713;</span>
          </div>
        );
      }
    });
  };

  // Icon component for Accordion
  function Icon({ id, open }) {
    return (
      <ChevronDownIcon
        className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`}
      />
    );
  }

  // Card component for each seat entry
  const SeatCard = ({ seat, index, allocationBoard }) => {
    const vacantSeats = seat.s001_vacantseats || 0;
    const seatColor = vacantSeats > 0 ? "text-green-600" : "text-red-600";
    
    return (
      <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4 mb-3 hover:shadow-lg transition-shadow">
        {/* Institute Name - Prominent */}
        <div className="mb-3">
          <h3 className="text-base font-bold text-primary-900 leading-tight">
            {seat.s001_instname}
          </h3>
          <p className="text-xs text-gray-500 mt-1">Code: {seat.s001_instcode}</p>
        </div>

        {/* Branch */}
        <div className="mb-3 pb-3 border-b border-gray-200">
          <p className="text-sm font-semibold text-secondary-900">
            {seat.s001_branchcode ? `(${seat.s001_branchcode}) ` : ''}{seat.s001_branchname}
          </p>
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          {seat.s001_statequota && (
            <div>
              <p className="text-xs text-gray-500">Institute Type</p>
              <p className="text-sm font-medium text-secondary-900">{seat.s001_statequota}</p>
            </div>
          )}
          {seat.s001_statecriteria && (
            <div>
              <p className="text-xs text-gray-500">State Criteria</p>
              <p className="text-sm font-medium text-secondary-900">{seat.s001_statecriteria}</p>
            </div>
          )}
          {allocationBoard !== 'COMEDK' && seat.s001_category && (
            <div>
              <p className="text-xs text-gray-500">Category</p>
              <p className="text-sm font-medium text-secondary-900">{seat.s001_category}</p>
            </div>
          )}
          {allocationBoard !== 'COMEDK' && seat.s001_gender && (
            <div>
              <p className="text-xs text-gray-500">Gender</p>
              <p className="text-sm font-medium text-secondary-900">{seat.s001_gender}</p>
            </div>
          )}
          {allocationBoard !== 'COMEDK' && seat.s001_common && (
            <div>
              <p className="text-xs text-gray-500">Common</p>
              <p className="text-sm font-medium text-secondary-900">{seat.s001_common || 'N/A'}</p>
            </div>
          )}
          {allocationBoard === 'MHTCET' && seat.s001_summary && (
            <div className="col-span-2">
              <p className="text-xs text-gray-500">Summary</p>
              <p className="text-sm font-medium text-secondary-900">{seat.s001_summary || 'N/A'}</p>
            </div>
          )}
        </div>

        {/* Vacant Seats - Highlighted */}
        <div className="mt-3 pt-3 border-t-2 border-primary-200 bg-primary-50 rounded p-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-600">Vacant Seats</span>
            <span className={`text-2xl font-bold ${seatColor}`}>{vacantSeats}</span>
          </div>
        </div>
      </div>
    );
  };

  const formatReleaseDate = (dateString) => {
    if (!dateString) return "No Date";

    const dateObj = new Date(dateString);
    return dateObj.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };
  
  // Sort and paginate data
  const sortedData = useMemo(() => {
    if (!data || !sortBy) return data || [];
    
    const sorted = [...data].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';
      
      // Handle numeric values (vacant seats)
      if (sortBy === 's001_vacantseats') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }
      
      // Handle string values
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [data, sortBy, sortOrder]);

  // Pagination logic
  const totalPages = sortedData ? Math.ceil(sortedData.length / itemsPerPage) : 0;
  const startIndex = currentPage * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = sortedData ? sortedData.slice(startIndex, endIndex) : [];

  return (
    <Fragment>
      <div className="flex flex-col w-full h-auto bg-gray-50">
        {status === "pending" ? (
          <div className="flex w-full justify-center items-center min-h-[60vh]">
            <LoadingPage invokedFrom="seats" />
          </div>
        ) : status === "error" ? (
          <div className="w-full h-auto">
            <ErrorPage 
              emessage={
                error?.response?.data?.detail || 
                error?.response?.data?.message || 
                error?.message || 
                "An error occurred while fetching seats data"
              } 
              invokedFrom="seats" 
            />
          </div>
        ) : (
          data && (
            <div className="w-full">
              {/* Sticky Header Section */}
              <div className="sticky top-0 z-10 bg-white shadow-md border-b border-gray-200">
                <div className="p-3 space-y-3">
                  {/* Collapsible Filter Section */}
                  <Accordion open={filterOpen} icon={<Icon id="filter" open={filterOpen} />}>
                    <AccordionHeader
                      onClick={() => setFilterOpen(!filterOpen)}
                      className="border-2 border-primary-500 rounded-lg shadow-sm py-3 px-4 bg-primary-50 hover:bg-primary-100 transition-colors"
                    >
                      <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                          <FunnelIcon className="h-5 w-5 text-primary-600" />
                          <span className="text-sm font-semibold text-primary-900">Filters</span>
                          {Object.keys(searchObject).some(key => searchObject[key] !== null && searchObject[key] !== undefined) && (
                            <span className="bg-primary-500 text-white text-xs rounded-full px-2 py-0.5 font-semibold">
                              {Object.values(searchObject).filter(v => v !== null && v !== undefined).length}
                            </span>
                          )}
                        </div>
                      </div>
                    </AccordionHeader>
                    <AccordionBody className="pt-4 pb-4 px-2">
                      <div className="bg-white rounded-lg border border-primary-200 p-3">
                        <SeatsVerticalPanel />
                      </div>
                    </AccordionBody>
                  </Accordion>

                  {/* Search Criteria Chips */}
                  {Object.keys(searchObject).some(key => searchObject[key] !== null && searchObject[key] !== undefined) && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm">📌</span>
                      <div className="flex flex-wrap gap-2">
                        <ShowSearchCriteria/>
                      </div>
                    </div>
                  )}

                  {/* Available Seats - Prominent Display */}
                  <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-lg p-3 border-2 border-primary-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-600 font-medium">Total Available Seats</p>
                        <p className="text-xs text-gray-500 mt-1">Release Date: {formatReleaseDate(data[0]?.s001_releasedate)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-bold text-primary-900">{matchedRow() || 0}</p>
                        <p className="text-xs text-blue-700 font-semibold">seats available</p>
                      </div>
                    </div>
                  </div>

                  {/* Sort Controls */}
                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="flex flex-wrap gap-2 items-center">
                      <span className="text-xs font-semibold text-gray-700 mr-1">Sort by:</span>
                      <button
                        onClick={() => handleSort('s001_instname')}
                        className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-colors flex items-center gap-1 ${
                          sortBy === 's001_instname'
                            ? 'bg-primary-500 text-white border-primary-500'
                            : 'bg-white text-primary-900 border-primary-500 hover:bg-primary-50'
                        }`}
                      >
                        Institute
                        {getSortIcon('s001_instname')}
                      </button>
                      <button
                        onClick={() => handleSort('s001_branchname')}
                        className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-colors flex items-center gap-1 ${
                          sortBy === 's001_branchname'
                            ? 'bg-primary-500 text-white border-primary-500'
                            : 'bg-white text-primary-900 border-primary-500 hover:bg-primary-50'
                        }`}
                      >
                        Branch
                        {getSortIcon('s001_branchname')}
                      </button>
                      <button
                        onClick={() => handleSort('s001_vacantseats')}
                        className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-colors flex items-center gap-1 ${
                          sortBy === 's001_vacantseats'
                            ? 'bg-primary-500 text-white border-primary-500'
                            : 'bg-white text-primary-900 border-primary-500 hover:bg-primary-50'
                        }`}
                      >
                        Vacant Seats
                        {getSortIcon('s001_vacantseats')}
                      </button>
                      {data && data[0]?.s001_allocationboard !== 'COMEDK' && (
                        <>
                          <button
                            onClick={() => handleSort('s001_category')}
                            className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-colors flex items-center gap-1 ${
                              sortBy === 's001_category'
                                ? 'bg-primary-500 text-white border-primary-500'
                                : 'bg-white text-primary-900 border-primary-500 hover:bg-primary-50'
                            }`}
                          >
                            Category
                            {getSortIcon('s001_category')}
                          </button>
                          <button
                            onClick={() => handleSort('s001_gender')}
                            className={`px-3 py-1.5 text-xs rounded-lg border-2 transition-colors flex items-center gap-1 ${
                              sortBy === 's001_gender'
                                ? 'bg-primary-500 text-white border-primary-500'
                                : 'bg-white text-primary-900 border-primary-500 hover:bg-primary-50'
                            }`}
                          >
                            Gender
                            {getSortIcon('s001_gender')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Cards Section */}
              <div className="p-3 pb-20">
                {paginatedData.length > 0 ? (
                  <div className="space-y-3">
                    {paginatedData.map((seat, index) => (
                      <SeatCard 
                        key={index} 
                        seat={seat} 
                        index={startIndex + index}
                        allocationBoard={data[0]?.s001_allocationboard}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500 text-base">No seats found</p>
                    <p className="text-gray-400 text-sm mt-2">Try adjusting your filters</p>
                  </div>
                )}
              </div>

              {/* Pagination - Sticky Bottom */}
              {totalPages > 1 && (
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-10">
                  <div className="flex items-center justify-between max-w-md mx-auto p-3">
                    <Button
                      size="sm"
                      variant="outlined"
                      className="px-4 py-2 text-xs"
                      onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                      disabled={currentPage === 0}
                    >
                      Previous
                    </Button>
                    <span className="text-sm font-semibold text-gray-700">
                      Page {currentPage + 1} of {totalPages}
                    </span>
                    <Button
                      size="sm"
                      variant="outlined"
                      className="px-4 py-2 text-xs"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                      disabled={currentPage >= totalPages - 1}
                    >
                      Next
                    </Button>
                  </div>
                  {/* Disclaimer - Below Pagination Footer */}
                  <div className="px-3 pb-3 pt-2 border-t border-gray-100">
                    <div className="text-xs text-gray-600 italic text-center leading-relaxed">
                      <span className="font-semibold text-gray-700">🔔 Disclaimer:</span> The information presented on this site is intended solely for general reference. While we strive to aggregate important notices from various examination boards in one convenient location, students are advised to refer to the official board websites for the most accurate, detailed, and up-to-date information. 📚
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        )}
      </div>
    </Fragment>
  );
};

